// Wave C — Recent | A–Z switcher for the Songs panel. Pure DOM builder: no
// localStorage, no i18n import — labels are explicit params, exactly like
// empty-state.ts's buildEmptyState. library-view.ts owns persistence (reads/
// writes the choice) and translation (t()); this module only knows how to
// build and wire the widget itself.
//
// Reuses the DS <bw-segmented-control> — the same component driving the
// Setlists/Songs tab bar above it — rather than hand-rolling a second kind of
// toggle (invariant: never duplicate a bw-* component, see
// packages/ui-components/README.md). The caption underneath is not
// decorative: Sergey's decision is that the list sorts by "last opened by
// ANYONE on the team" without naming who, so a song jumping to the top with
// no visible cause needs an explanation right there or it reads as a bug.
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-segmented-control.css';
import type { SongSort } from './song-rows.js';

export interface SortControlLabels {
  recent: string;
  alpha: string;
  /** "Last opened by anyone in the team" — also doubles as the control's aria-label. */
  caption: string;
}

export interface SortControlOpts {
  value: SongSort;
  labels: SortControlLabels;
  onChange: (next: SongSort) => void;
}

/** Builds the sort switcher + caption. Does not append itself anywhere — the
 * caller places the returned element (mirrors buildEmptyState/buildViewersPanel). */
export function buildSortControl(opts: SortControlOpts): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lib-sort-control';
  wrap.dataset.role = 'songs-sort-wrap';

  const seg = document.createElement('bw-segmented-control') as HTMLElement & {
    segments: Array<{ value: string; label: string }>;
    value: string;
  };
  seg.dataset.role = 'songs-sort';
  seg.setAttribute('size', 'sm');
  seg.setAttribute('aria-label', opts.labels.caption);
  seg.segments = [
    { value: 'recent', label: opts.labels.recent },
    { value: 'alpha', label: opts.labels.alpha },
  ];
  seg.value = opts.value;
  seg.addEventListener('bw-segmented-change', (e) => {
    const v = (e as CustomEvent<{ value: string }>).detail?.value;
    opts.onChange(v === 'alpha' ? 'alpha' : 'recent');
  });
  wrap.appendChild(seg);

  const caption = document.createElement('span');
  caption.dataset.role = 'songs-sort-caption';
  caption.className = 'lib-sort-caption';
  caption.textContent = opts.labels.caption;
  wrap.appendChild(caption);

  return wrap;
}
